import { Link, useNavigate } from "react-router";
import { usePolling } from "../lib/use-polling";
import { api } from "../lib/api";
import { PageHelp } from "../components/PageHelp";
import { PageHeader } from "../components/primitives/PageHeader";
import { Table, type Column } from "../components/primitives/Table";
import { Badge } from "../components/primitives/Badge";
import { Button } from "../components/primitives/Button";
import { Pulse } from "../components/primitives/Pulse";
import { InfoTip } from "../components/primitives/InfoTip";
import { useConfirm } from "../components/primitives/Confirm";
import { vendorLabel } from "../lib/friendly-labels";

interface ConnectionRow {
  id: string;
  name?: string;
  label?: string;
  connectorType: string;
  organizationId?: string;
  environment?: string | null;
}

export interface PipelineRef {
  id: string;
  name: string;
  sourceInstanceId?: string | null;
  destInstanceId?: string | null;
  sourceInstance?: { id: string } | null;
  destInstance?: { id: string } | null;
}

/** Pipelines whose source or destination is this connection, in either id shape the API emits. */
export function pipelinesUsing(pipelines: PipelineRef[], instanceId: string): PipelineRef[] {
  return pipelines.filter(
    (p) =>
      p.sourceInstanceId === instanceId ||
      p.destInstanceId === instanceId ||
      p.sourceInstance?.id === instanceId ||
      p.destInstance?.id === instanceId,
  );
}

/**
 * Every connection across clients (Build group).
 *
 * Deleting one also removes its credentials and stops every pipeline using it,
 * with nothing to undo it — which is what makes deletion Tier 2 (spec §7).
 */
export function Connections() {
  const nav = useNavigate();
  const confirm = useConfirm();
  const instances = usePolling(() => api.listConnectorInstances(), 30000);
  const connectors = usePolling(() => api.listConnectors(), 60000);
  const pipelinesQ = usePolling(() => api.listPipelines(), 60000);
  const orgsQ = usePolling(() => api.listOrganizations(), 60000);

  if (instances.status === "loading" || connectors.status === "loading") return <Pulse />;

  const connectorList = connectors.data?.connectors ?? [];
  const webhookTypes = new Set(
    connectorList
      .filter((c: { ingest?: { webhook?: unknown } }) => c.ingest?.webhook)
      .map((c: { connectorType: string }) => c.connectorType),
  );

  /** Delete a connection, after naming the pipelines that will stop working.
   *  When the pipeline list has not loaded the wording says so rather than
   *  claiming nothing uses it. */
  async function remove(row: ConnectionRow) {
    const name = row.label ?? row.name ?? row.id;
    const refs = pipelinesQ.status === "ready" ? pipelinesUsing(pipelinesQ.data?.pipelines ?? [], row.id) : null;
    const body =
      refs === null
        ? "Any pipeline that uses this connection will stop working. Its credentials are removed from Secrets Manager. This cannot be undone."
        : refs.length === 0
          ? "No pipelines use this connection. Its credentials are removed from Secrets Manager. This cannot be undone."
          : `${refs.length === 1 ? "This pipeline uses" : "These pipelines use"} this connection and will stop working. Its credentials are removed from Secrets Manager. This cannot be undone.`;
    await confirm({
      tier: 2,
      title: `Delete ${name}?`,
      body,
      items: refs && refs.length > 0 ? refs.map((p) => ({ label: p.name, meta: p.id.slice(0, 8) })) : undefined,
      confirmLabel: `Delete ${name}`,
      typedPhrase: name,
      onConfirm: async () => { await api.deleteConnectorInstance(row.id); },
    });
  }

  const rows: ConnectionRow[] = instances.data?.instances ?? [];
  // Client names read better than id prefixes; the id stays as the fallback until the clients load.
  const clientNames = new Map<string, string>(
    (orgsQ.data?.organizations ?? []).map((o: { id: string; name: string }) => [o.id, o.name] as const),
  );
  const columns: Column<ConnectionRow>[] = [
    { key: "name", label: "NAME", mono: true, render: (r) => r.label ?? r.name ?? String(r.id).slice(0, 8) },
    { key: "vendor", label: "VENDOR", render: (r) => vendorLabel(r.connectorType, connectorList) },
    {
      key: "mode",
      label: "MODE",
      render: (r) => (
        <Badge tone={webhookTypes.has(r.connectorType) ? "ok" : "muted"}>
          {webhookTypes.has(r.connectorType) ? "Push" : "Pull"}
        </Badge>
      ),
    },
    {
      key: "client",
      label: "CLIENT",
      render: (r) =>
        r.organizationId ? (
          <Link to={`/clients/${r.organizationId}`} onClick={(e) => e.stopPropagation()}>
            {clientNames.get(r.organizationId) ?? <span className="mono">{String(r.organizationId).slice(0, 8)}</span>}
          </Link>
        ) : "—",
    },
    { key: "env", label: "ENVIRONMENT", render: (r) => r.environment ?? "Any" },
    {
      key: "actions",
      label: "",
      render: (r) => (
        <Button
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            void remove(r);
          }}
        >
          Delete
        </Button>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        eyebrow={
          <>
            Build
            <InfoTip termKey="connection" />
          </>
        }
        title="Connections"
        summary={`${rows.length} ${rows.length === 1 ? "connection" : "connections"} across all clients`}
        actions={<Link to="/connections/new"><Button variant="primary">New connection</Button></Link>}
      />
      <PageHelp />
      <Table columns={columns} rows={rows} rowKey={(r) => r.id} onRowClick={(r) => nav(`/connections/${r.id}`)} />
    </div>
  );
}
